/**
 * BEEZFY PREMIUM SWATCH SYSTEM
 * Advanced variant selector with full admin integration
 * 
 * Features:
 * - Dynamic style application from admin settings
 * - Color, image, and text swatches
 * - Tooltips and labels
 * - Zoom and hover effects
 * - Limit and show more functionality
 * - Full responsive support
 */


// Check if already defined to avoid duplicate declaration
if (typeof BeezfySwatchSystem === 'undefined') {
  class BeezfySwatchSystem {
    constructor() {
      this.settings = window.beezfySwatchSettings || {};
      this.initialized = false;
      this.swatchContainers = [];
      this.colorOptionNames = [];
      this.sizeOptionNames = [];
      this.colorAttributeNames = [];
      this.sizeAttributeNames = [];
      this.bundleTags = [];
      this.init();
    }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  initialize() {
    if (this.initialized) return;

    // Initialization started

    try {
      // Load settings from admin
      this.loadAdminSettings();
      
      // Apply global styles - DEPRECATED. This is now handled in layout/theme.liquid
      // this.applyGlobalStyles();
      
      // Initialize all swatch containers
      this.initializeSwatchContainers();

      // Setup event listeners
      this.setupEventListeners();

      // Setup mutation observer for dynamic content
      this.setupMutationObserver();

      this.initialized = true;
      // Initialization complete
    } catch (error) {
      console.error('❌ Beezfy Swatch System: Initialization error', error);
    }
  }

  connectDataStore() {
    if (this.dataStore) return;
    if (window.BeezfySwatchDataStore) {
      this.dataStore = window.BeezfySwatchDataStore;
    }
  }

  loadAdminSettings() {
    // Get settings from Shopify theme settings
    const root = document.documentElement;
    const theme = window.themeSettings?.swatch || {};
    const swatchConfig = window.BeezfyTheme?.swatches || {};

    this.settings = {
      // Global settings
      mode: root.dataset.swatchMode || theme.mode || 'color',
      type: root.dataset.swatchType || theme.type || 'all',
      shape: root.dataset.swatchShape || theme.shape || 'circle',
      size: root.dataset.swatchSize || theme.size || 'medium',
      style: root.dataset.swatchStyle || theme.style || 'style-1',
      event: root.dataset.swatchEvent || theme.event || 'click',

      // Limit settings
      enableLimit: (root.dataset.swatchEnableLimit || theme.enableLimit) === 'true',
      maximum: parseInt(root.dataset.swatchMaximum || theme.maximum) || 6,
      limitAction: root.dataset.swatchLimitAction || theme.limitAction || 'show_more',

      // Visual settings
      showTooltips: (root.dataset.swatchShowTooltips || theme.showTooltips) !== 'false',
      showLabels: (root.dataset.swatchShowLabels || theme.showLabels) === 'true',
      enableZoom: (root.dataset.swatchEnableZoom || theme.enableZoom) === 'true',

      // Style settings
      borderWidth: root.dataset.swatchBorderWidth || theme.borderWidth || '2px',
      borderRadius: root.dataset.swatchBorderRadius || theme.borderRadius || '50%',
      borderColor: root.dataset.swatchBorderColor || theme.borderColor || '#e5e5e5',
      selectedBorderColor: root.dataset.swatchSelectedBorderColor || theme.selectedBorderColor || '#000000',
      selectedBorderWidth: root.dataset.swatchSelectedBorderWidth || theme.selectedBorderWidth || '3px',

      // Label settings
      labelPosition: root.dataset.swatchLabelPosition || theme.labelPosition || 'bottom',
      labelFontSize: root.dataset.swatchLabelFontSize || theme.labelFontSize || '11px',
      labelFontWeight: root.dataset.swatchLabelFontWeight || theme.labelFontWeight || '500',
      labelColor: root.dataset.swatchLabelColor || theme.labelColor || '#666666',
      selectedLabelColor: root.dataset.swatchSelectedLabelColor || theme.selectedLabelColor || '#000000',

      // Spacing and effects
      spacing: root.dataset.swatchSpacing || theme.spacing || '12px',
      hoverScale: root.dataset.swatchHoverScale || theme.hoverScale || '1.05',
      hoverLift: root.dataset.swatchHoverLift || theme.hoverLift || '2px',
      shadowBlur: root.dataset.swatchShadowBlur || theme.shadowBlur || '8px',
      shadowOpacity: root.dataset.swatchShadowOpacity || theme.shadowOpacity || '0.15',

      // Image source
      imageSource: root.dataset.swatchImageSource || theme.imageSource || 'variant_img'
    };

    this.prepareOptionDictionaries(swatchConfig);

    // Settings loaded
  }

  prepareOptionDictionaries(swatchConfig) {
    const toArray = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        return value.split(',').map((item) => item && item.trim()).filter(Boolean);
      }
      return [];
    };

    this.colorOptionNames = toArray(swatchConfig.colorOptionNames || swatchConfig.color_option_names)
      .map((name) => name.toLowerCase());

    if (!this.colorOptionNames.length) {
      this.colorOptionNames = ['color', 'colour', 'colore', 'couleur', 'farbe', 'cor', 'stile', 'style'];
    }

    this.colorAttributeNames = toArray(swatchConfig.colorAttributeNames || swatchConfig.color_attribute_names)
      .map((name) => name.toLowerCase());

    if (!this.colorAttributeNames.length) {
      this.colorAttributeNames = ['stile', 'style', 'shade', 'tonalita', 'tonalite', 'tone', 'nuance', 'teint', 'tinta'];
    }

    this.sizeOptionNames = toArray(swatchConfig.sizeOptionNames || swatchConfig.size_option_names)
      .map((name) => name.toLowerCase());

    if (!this.sizeOptionNames.length) {
      this.sizeOptionNames = ['size', 'taglia', 'taille', 'tamaño', 'größe', 'groesse'];
    }

    this.sizeAttributeNames = toArray(swatchConfig.sizeAttributeNames || swatchConfig.size_attribute_names)
      .map((name) => name.toLowerCase());

    if (!this.sizeAttributeNames.length) {
      this.sizeAttributeNames = ['size', 'taglia', 'taille', 'tamaño', 'größe', 'groesse', 'dimension', 'maß', 'mas'];
    }

    this.bundleTags = toArray(swatchConfig.bundleTags).map((tag) => tag.toLowerCase());
  }

  // The applyGlobalStyles and getShapeRadius functions have been removed.
  // This functionality is now handled entirely by CSS variables
  // defined in layout/theme.liquid, based on the theme settings.

  initializeSwatchContainers() {
    // Ensure datastore connection is ready before processing containers
    this.connectDataStore();

    // Find all variant selectors
    const selectors = document.querySelectorAll('variant-selects, .beezfy-variant-selector');

    selectors.forEach(selector => {
      this.processSwatchContainer(selector);
    });

    const standaloneContainers = document.querySelectorAll('[data-swatches-container]');
    standaloneContainers.forEach((container) => {
      if (container.closest('variant-selects, .beezfy-variant-selector')) return;
      if (!this.dataStore) {
        this.dataStore = window.BeezfySwatchDataStore || null;
      }
      if (this.dataStore) {
        this.dataStore.enrichContainer(container);
      }
    });
  }

  processSwatchContainer(container) {
    // Get all swatch fieldsets
    const fieldsets = container.querySelectorAll('.product-form__input--swatch, .beezfy-swatch-fieldset');

    fieldsets.forEach(fieldset => {
      this.enhanceSwatchFieldset(fieldset);
    });

    if (!this.swatchContainers.includes(container)) {
      this.swatchContainers.push(container);
    }

    if (!this.dataStore) {
      this.connectDataStore();
    }

    if (this.dataStore) {
      this.dataStore.enrichContainer(container);
    }
  }

  refreshSwatchContainers() {
    if (!this.dataStore || !this.swatchContainers.length) return;
    this.swatchContainers.forEach((container) => {
      if (container && container.isConnected) {
        this.dataStore.enrichContainer(container);
      }
    });
  }

  enhanceSwatchFieldset(fieldset) {
    // Add Beezfy classes
    fieldset.classList.add('beezfy-swatch-fieldset');

    // Get all swatches in this fieldset
    const swatches = fieldset.querySelectorAll('.swatch-input__label, .beezfy-swatch-label');
    const swatchContainer = fieldset.querySelector('.beezfy-swatch-container') || this.createSwatchContainer(fieldset);

    const optionName = swatchContainer?.dataset?.optionName || fieldset.dataset.optionName || fieldset.getAttribute('data-option-name') || '';
    const optionType = this.detectOptionType(optionName);

    if (swatchContainer) {
      swatchContainer.dataset.optionType = optionType;
      if (!swatchContainer.dataset.optionName && optionName) {
        swatchContainer.dataset.optionName = optionName;
      }
    }

    // Apply settings to container
    swatchContainer.classList.add(`beezfy-swatch-container--${this.settings.style}`);
    swatchContainer.classList.add(`beezfy-swatch-container--${this.settings.size}`);

    if (this.settings.enableZoom) {
      swatchContainer.classList.add('beezfy-swatch-container--zoom');
    }
    
    // Process each swatch
    swatches.forEach((swatch, index) => {
      this.enhanceSwatch(swatch, index);

      if (optionType === 'color') {
        swatch.dataset.optionType = 'color';
        if (!swatch.dataset.optionName && optionName) {
          swatch.dataset.optionName = optionName;
        }
      }

      // Handle limit
      if (this.settings.enableLimit && index >= this.settings.maximum) {
        swatch.style.display = 'none';
        swatch.dataset.hidden = 'true';
      }
    });

    // Add show more button if needed
    if (this.settings.enableLimit && swatches.length > this.settings.maximum) {
      this.addShowMoreButton(swatchContainer, swatches);
    }
  }

  createSwatchContainer(fieldset) {
    const container = document.createElement('div');
    container.className = 'beezfy-swatch-container';

    // Move all swatch items into container
    const items = fieldset.querySelectorAll('.swatch-input');
    items.forEach(item => {
      container.appendChild(item.parentElement);
    });

    fieldset.appendChild(container);
    return container;
  }

  enhanceSwatch(swatchLabel, index) {
    const swatchItem = swatchLabel.closest('.swatch-input') || swatchLabel.parentElement;
    const swatchInput = swatchItem.querySelector('input[type="radio"], input[type="checkbox"]');
    const swatchElement = swatchLabel.querySelector('.swatch, .hdt-product-form_value');
    
    if (!swatchItem || !swatchElement) return;
    
    // Add Beezfy classes
    swatchItem.classList.add('beezfy-swatch-item');
    swatchItem.classList.add(`beezfy-swatch-item--label-${this.settings.labelPosition}`);

    swatchLabel.classList.add('beezfy-swatch-label');
    
    swatchElement.classList.add('beezfy-swatch');
    swatchElement.classList.add(`beezfy-swatch--${this.settings.size}`);
    swatchElement.classList.add(`beezfy-swatch--${this.settings.shape}`);

    // Detect swatch type
    const swatchType = this.detectSwatchType(swatchElement);
    swatchElement.classList.add(`beezfy-swatch--${swatchType}`);
    
    // Apply custom styles based on type
    this.applySwatchStyles(swatchElement, swatchType);
    
    // Add tooltip if enabled
    if (this.settings.showTooltips) {
      this.addTooltip(swatchLabel, swatchInput);
    }
    
    // Add label if enabled
    if (this.settings.showLabels) {
      this.addLabel(swatchLabel, swatchInput);
    }

    // Handle interaction event
    if (this.settings.event === 'hover') {
      swatchLabel.addEventListener('mouseenter', () => {
        if (!swatchInput.disabled) {
          swatchInput.click();
        }
      });
    }
  }

  detectSwatchType(element) {
    if (element.querySelector('img')) {
      return 'image';
    } else if (element.style.background || element.style.backgroundColor) {
      return 'color';
    } else if (element.textContent.trim()) {
      return 'text';
    }
    return 'default';
  }

  normalizeToken(value) {
    if (!value && value !== 0) return '';
    return value
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  extractAttributeToken(optionName = '') {
    if (!optionName) return '';
    const raw = optionName.toString();
    const parenthesisMatch = raw.match(/\(([^)]+)\)\s*$/);
    if (parenthesisMatch && parenthesisMatch[1]) {
      return this.normalizeToken(parenthesisMatch[1]);
    }

    const colonParts = raw.split(':');
    if (colonParts.length > 1) {
      return this.normalizeToken(colonParts[colonParts.length - 1]);
    }

    return '';
  }

  extractOptionTokens(optionName = '') {
    if (!optionName) return [];
    const raw = optionName.toString();
    const tokens = new Set();
    const push = (value) => {
      const normalized = this.normalizeToken(value);
      if (normalized) tokens.add(normalized);
    };

    push(raw);

    raw.replace(/\(([^)]+)\)/g, (_, group) => {
      push(group);
      return '';
    });

    raw.split(/[\/|]+/).forEach(push);

    const sanitized = raw.replace(/[()]/g, ' ');
    sanitized.split(/[–—-]/).forEach(push);

    return Array.from(tokens);
  }

  detectOptionType(optionName = '') {
    const attributeToken = this.extractAttributeToken(optionName);
    if (attributeToken) {
      if (this.colorAttributeNames.includes(attributeToken) || this.colorOptionNames.includes(attributeToken)) {
        return 'color';
      }
      if (this.sizeAttributeNames.includes(attributeToken) || this.sizeOptionNames.includes(attributeToken)) {
        return 'size';
      }
    }

    const tokens = this.extractOptionTokens(optionName);
    if (!tokens.length) return 'text';

    const hasColorAttribute = tokens.some((token) => this.colorAttributeNames.includes(token));
    const hasColorToken = tokens.some((token) => this.colorOptionNames.includes(token));
    const hasSizeAttribute = tokens.some((token) => this.sizeAttributeNames.includes(token));
    const hasSizeToken = tokens.some((token) => this.sizeOptionNames.includes(token));

    if ((hasColorAttribute || hasColorToken) && !(hasSizeAttribute || hasSizeToken)) {
      return 'color';
    }

    if ((hasSizeAttribute || hasSizeToken) && !(hasColorAttribute || hasColorToken)) {
      return 'size';
    }

    if (hasColorAttribute && !hasSizeAttribute) {
      return 'color';
    }

    if (hasSizeAttribute && !hasColorAttribute) {
      return 'size';
    }

    if (hasColorAttribute || hasColorToken) {
      return 'color';
    }

    if (hasSizeAttribute || hasSizeToken) {
      return 'size';
    }

    return 'text';
  }

  applySwatchStyles(element, type) {
    if (type === 'color') {
      const bgColor = element.style.backgroundColor || element.dataset.color;
      if (bgColor) {
        element.style.setProperty('--swatch-color', bgColor);
      }
    } else if (type === 'gradient') {
      const gradient = element.dataset.gradient;
      if (gradient) {
        element.style.setProperty('--swatch-gradient', gradient);
      }
    }
  }

  addTooltip(swatchLabel, input) {
    if (swatchLabel.querySelector('.beezfy-swatch-tooltip')) return;
    
    const tooltip = document.createElement('div');
    tooltip.className = 'beezfy-swatch-tooltip';
    tooltip.textContent = input.value || input.dataset.optionValue || '';
    
    swatchLabel.appendChild(tooltip);
  }

  addLabel(swatchLabel, input) {
    if (swatchLabel.querySelector('.beezfy-swatch-text')) return;
    
    const label = document.createElement('span');
    label.className = 'beezfy-swatch-text';
    label.textContent = input.value || input.dataset.optionValue || '';
    
    swatchLabel.appendChild(label);
  }

  addShowMoreButton(container, swatches) {
    if (container.querySelector('.beezfy-swatch-more')) return;
    
    const button = document.createElement('button');
    button.className = 'beezfy-swatch-more';
    button.type = 'button';
    
    const hiddenCount = swatches.length - this.settings.maximum;
    button.textContent = `+${hiddenCount} more`;
    
    button.addEventListener('click', () => {
      swatches.forEach(swatch => {
        if (swatch.dataset.hidden === 'true') {
          swatch.style.display = '';
          swatch.dataset.hidden = 'false';
        }
      });
      button.style.display = 'none';
    });
    
    container.appendChild(button);
  }

  setupEventListeners() {
    // Listen for variant changes
    document.addEventListener('variant:changed', (event) => {
      this.handleVariantChange(event.detail);
    });
    
    // Listen for product quick view
    document.addEventListener('quickview:loaded', () => {
      setTimeout(() => this.initializeSwatchContainers(), 100);
    });
    
    // Listen for collection filters
    document.addEventListener('collection:filtered', () => {
      setTimeout(() => this.initializeSwatchContainers(), 100);
    });
  }

  setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.matches && (node.matches('variant-selects') || node.matches('.beezfy-variant-selector'))) {
              this.processSwatchContainer(node);
            } else if (node.querySelector) {
              const selectors = node.querySelectorAll('variant-selects, .beezfy-variant-selector');
              selectors.forEach(selector => this.processSwatchContainer(selector));
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  handleVariantChange(variant) {
    // Variant changed
    // Update selected states
    this.updateSelectedStates();
  }

  updateSelectedStates() {
    // Reset previous selections
    document.querySelectorAll('.beezfy-swatch-input').forEach((input) => {
      input.removeAttribute('checked');
    });

    document.querySelectorAll('[data-swatch]').forEach((swatch) => {
      swatch.classList.remove('beezfy-swatch--selected', 'beezfy-swatch--active', 'bz-swatch--active');
      swatch.setAttribute('aria-pressed', 'false');
      const label = swatch.closest('.beezfy-swatch-label');
      if (label) {
        label.classList.remove('beezfy-swatch-label--selected', 'beezfy-swatch-label--active');
      }
    });

    // Update visual states for the current selection
    document.querySelectorAll('.beezfy-swatch-input:checked').forEach(input => {
      const label = input.nextElementSibling;
      if (!label || !label.classList.contains('beezfy-swatch-label')) return;

      label.classList.add('beezfy-swatch-label--selected');
      const swatch = label.querySelector('.beezfy-swatch');
      if (swatch) {
        swatch.classList.add('beezfy-swatch--selected', 'beezfy-swatch--active', 'bz-swatch--active');
        swatch.setAttribute('aria-pressed', 'true');
      }

      label.classList.add('beezfy-swatch-label--active');
      input.setAttribute('checked', 'checked');
    });
  }

  // Public API
  refresh() {
    // System refreshing
    this.loadAdminSettings();
    this.applyGlobalStyles();
    this.initializeSwatchContainers();
  }

  updateSettings(newSettings) {
    Object.assign(this.settings, newSettings);
    this.applyGlobalStyles();
    this.refresh();
  }

  renderSwatch(optionName, value, options = {}) {
    // This function is now deprecated for styling.
    // Styling is handled by global CSS variables in layout/theme.liquid and beezfy-swatch-system.css.
    // This function will now only return a basic structure.
    const normalized = String(value).toLowerCase().replace(/\s+/g, '-');
    const root = getComputedStyle(document.documentElement);
    let image = root.getPropertyValue(`--swatch-image-${normalized}`).trim().replace(/^['"]|['"]$/g, '');
    let color = root.getPropertyValue(`--swatch-color-${normalized}`).trim();

    let content = '';
    if (image) {
      content = `<img class="beezfy-swatch__image" src="${image}" alt="${value}">`;
    } else if (color) {
      content = `<span class="beezfy-swatch__color" style="background-color: ${color};"></span>`;
    } else {
      content = `<span class="beezfy-swatch__text">${value.slice(0, 3)}</span>`;
    }

    // Return a simple span without any inline styles for size, shape, or radius.
    // The `.swatch` class will be styled by the global CSS.
    return `<span class="swatch">${content}</span>`;
  }
  }

  class BeezfySwatchDataStore {
    constructor(system) {
      this.system = system || null;
      this.productPromises = new Map();
      this.componentPromises = new Map();
      this.colorImageCache = new Map();
      this.variantMediaCache = new Map();
      this.bundleComponentsCache = new Map();
      this.enrichmentQueue = new WeakMap();
      this.colorOptionNames = (system?.colorOptionNames || []).slice();
      this.colorAttributeNames = (system?.colorAttributeNames || []).slice();
      this.sizeOptionNames = (system?.sizeOptionNames || []).slice();
      this.sizeAttributeNames = (system?.sizeAttributeNames || []).slice();
      if (!this.colorOptionNames.length) {
        this.colorOptionNames = ['color', 'colour', 'colore', 'couleur', 'farbe', 'cor', 'stile', 'style'];
      }
      if (!this.colorAttributeNames.length) {
        this.colorAttributeNames = ['stile', 'style', 'shade', 'tonalita', 'tonalite', 'tone', 'nuance', 'teint', 'tinta'];
      }
      if (!this.sizeOptionNames.length) {
        this.sizeOptionNames = ['size', 'taglia', 'taille', 'tamaño', 'größe', 'groesse'];
      }
      if (!this.sizeAttributeNames.length) {
        this.sizeAttributeNames = ['dimension', 'mas', 'maß', 'dimensione', 'medida'];
      }

      if (this.system) {
        this.system.dataStore = this;
        if (typeof this.system.refreshSwatchContainers === 'function') {
          this.system.refreshSwatchContainers();
        } else if (typeof this.system.initializeSwatchContainers === 'function') {
          this.system.initializeSwatchContainers();
        }
      }
    }

    enrichContainer(container) {
      if (!container || !(container instanceof Element)) return;

      if (this.enrichmentQueue.has(container)) {
        clearTimeout(this.enrichmentQueue.get(container));
      }

      const timeout = setTimeout(() => {
        this.enrichmentQueue.delete(container);
        this.applyBundleEnhancements(container);
      }, 0);

      this.enrichmentQueue.set(container, timeout);
    }

    async applyBundleEnhancements(container) {
      try {
        const dataset = container.dataset || {};
        const optionName = dataset.optionName || '';
        const optionType = dataset.optionType || this.system?.detectOptionType(optionName) || 'text';

        if (optionType !== 'color') return;

        const swatches = Array.from(container.querySelectorAll('[data-swatch]'));
        if (!swatches.length) return;

        const hasMissingMedia = swatches.some((swatch) => !swatch.dataset.swatchImage || !swatch.querySelector('img'));
        if (!hasMissingMedia) return;

        const productHandle = dataset.productHandle || this.findProductHandle(container);
        const productId = dataset.productId || this.findProductId(container);
        const bundleComponentHandle = dataset.bundleComponent || '';
        const bundleComponentTitle = dataset.bundleComponentTitle || '';

        if (!productHandle && !productId) return;

        const productData = this.getContextProductData(productHandle, productId);

        const valueMetadata = swatches.map((swatch) => ({
          value: swatch.dataset.optionValue || '',
          component: swatch.dataset.bundleComponent || bundleComponentHandle || '',
          label: swatch.dataset.swatchLabel || swatch.dataset.optionValue || '',
        }));

        const optionValues = valueMetadata.map((meta) => meta.value || '');

        const colorImages = await this.getColorImages({
          productHandle: productHandle || productId,
          optionName,
          optionValues,
          productData,
          bundleComponent: bundleComponentHandle,
          bundleComponentTitle,
          valueMetadata
        });

        if (!colorImages) return;

        swatches.forEach((swatch) => {
          const value = swatch.dataset.optionValue || '';
          const media = colorImages[this.normalizeLabel(value)];

          if (media?.image) {
            this.applyImageToSwatch(swatch, media);
          } else {
            this.applyTextFallback(swatch, value);
          }
        });
      } catch (error) {
        console.warn('[BeezfySwatchDataStore] Failed to enhance swatch container', error);
      }
    }

    findProductHandle(container) {
      if (!container) return null;
      if (container.dataset?.productHandle) return container.dataset.productHandle;
      const productWrapper = container.closest('[data-product-handle]');
      if (productWrapper?.dataset?.productHandle) return productWrapper.dataset.productHandle;
      return null;
    }

    findProductId(container) {
      if (!container) return null;
      if (container.dataset?.productId) return container.dataset.productId;
      const productWrapper = container.closest('[data-product-id]');
      if (productWrapper?.dataset?.productId) return productWrapper.dataset.productId;
      return null;
    }

    getContextProductData(handle, id) {
      const productPage = window.beezfyProductPage?.product;
      if (productPage) {
        if ((handle && productPage.handle === handle) || (id && String(productPage.id) === String(id))) {
          return productPage;
        }
      }

      if (handle) {
        const script = document.querySelector('script[type="application/json"][data-product-json]');
        if (script) {
          try {
            const parsed = JSON.parse(script.textContent);
            const productData = parsed.product || parsed;
            if (productData && (productData.handle === handle || String(productData.id) === String(id))) {
              return productData;
            }
          } catch (error) {
            console.warn('[BeezfySwatchDataStore] Unable to parse inline product JSON', error);
          }
        }
      }

      return null;
    }

    normalizeLabel(label) {
      if (!label) return '';
      return label
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    extractAttributeToken(optionName = '') {
      if (!optionName) return '';
      if (this.system?.extractAttributeToken) {
        return this.system.extractAttributeToken(optionName);
      }
      const raw = optionName.toString();
      const parenthesisMatch = raw.match(/\(([^)]+)\)\s*$/);
      if (parenthesisMatch && parenthesisMatch[1]) {
        return this.normalizeLabel(parenthesisMatch[1]);
      }
      const colonParts = raw.split(':');
      if (colonParts.length > 1) {
        return this.normalizeLabel(colonParts[colonParts.length - 1]);
      }
      return '';
    }

    extractOptionTokens(optionName = '') {
      if (!optionName) return [];
      if (this.system?.extractOptionTokens) {
        return this.system.extractOptionTokens(optionName);
      }

      const raw = optionName.toString();
      const tokens = new Set();
      const push = (value) => {
        const normalized = this.normalizeLabel(value);
        if (normalized) tokens.add(normalized);
      };

      push(raw);

      raw.replace(/\(([^)]+)\)/g, (_, group) => {
        push(group);
        return '';
      });

      raw.split(/[\/|]+/).forEach(push);

      const sanitized = raw.replace(/[()]/g, ' ');
      sanitized.split(/[–—-]/).forEach(push);

      return Array.from(tokens);
    }

    slugify(value) {
      return this.normalizeLabel(value).replace(/\s+/g, '-');
    }

    isLikelyComponentName(name) {
      const normalized = this.normalizeLabel(name);
      if (!normalized) return false;

      if (this.colorOptionNames.includes(normalized) || this.colorAttributeNames?.includes(normalized)) return false;

      const genericTokens = [
        'size', 'taglia', 'taille', 'talla', 'größe', 'groesse', 'grosse', 'colour', 'color', 'colore',
        'couleur', 'farbe', 'material', 'default', 'option', 'variant', 'style'
      ];

      if (genericTokens.includes(normalized)) return false;

      if (normalized.length <= 3) return false;

      const words = normalized.split(' ').filter(Boolean);
      if (words.length <= 1 && normalized.length <= 6) {
        return false;
      }

      return true;
    }

    extractComponentName(raw) {
      if (!raw) return '';

      let value = raw.toString().trim();
      if (!value) return '';

      const colonIndex = value.indexOf(':');
      if (colonIndex !== -1) {
        value = value.slice(0, colonIndex).trim();
      }

      const parenthesisMatch = value.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
      if (parenthesisMatch) {
        value = parenthesisMatch[1].trim();
      }

      if (!this.isLikelyComponentName(value)) return '';

      return value;
    }

    guessBundleComponentsFromProduct(product) {
      const guesses = [];
      const pushed = new Set();
      const pushGuess = (title, meta = {}) => {
        if (!title) return;
        const cleaned = title.toString().trim();
        if (!cleaned) return;
        const normalized = this.normalizeLabel(cleaned);
        if (!normalized) return;

        if (pushed.has(normalized)) return;
        pushed.add(normalized);

        const handle = this.slugify(cleaned);
        if (!handle) return;

        guesses.push({
          title: cleaned,
          handle,
          normalizedTitle: normalized,
          optionName: meta.optionName || null
        });
      };

      const optionsWithValues = Array.isArray(product.options_with_values)
        ? product.options_with_values
        : [];

      optionsWithValues.forEach((option) => {
        const optionName = typeof option === 'string' ? option : option?.name;
        const componentName = this.extractComponentName(optionName);
        if (componentName) {
          pushGuess(componentName, { optionName });
        }

        const values = Array.isArray(option?.values) ? option.values : [];
        values.forEach((value) => {
          const preColon = typeof value === 'string' ? value.split(':')[0] : value;
          const fromValue = this.extractComponentName(preColon);
          if (fromValue) {
            pushGuess(fromValue, { optionName });
          }
        });
      });

      if (!guesses.length && Array.isArray(product.options)) {
        product.options.forEach((option) => {
          const componentName = this.extractComponentName(option);
          if (componentName) {
            pushGuess(componentName, { optionName: option });
          }
        });
      }

      const variants = Array.isArray(product.variants) ? product.variants : [];
      variants.forEach((variant) => {
        const title = variant?.title;
        if (typeof title !== 'string') return;
        title.split('/').forEach((segment) => {
          const fromSegment = this.extractComponentName(segment);
          if (fromSegment) {
            pushGuess(fromSegment, { optionName: segment });
          }
        });
      });

      return guesses;
    }

    async getProduct(identifier, productData) {
      if (productData) return productData;
      if (!identifier) return null;

      const key = identifier.toString();
      if (this.productPromises.has(key)) {
        return this.productPromises.get(key);
      }

      const encoded = encodeURIComponent(key);
      const promise = fetch(`/products/${encoded}.js`)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          console.warn('[BeezfySwatchDataStore] Failed to load product', identifier, error);
          return null;
        });

      this.productPromises.set(key, promise);
      return promise;
    }

    async getColorImages({
      productHandle,
      optionName,
      optionValues = [],
      productData,
      bundleComponent,
      bundleComponentTitle,
      valueMetadata = []
    }) {
      if (!productHandle && !productData) return null;

      const keyBase = productHandle || (productData?.handle || productData?.id);
      if (!keyBase) return null;

      const optionKey = (optionName || '').toLowerCase();
      const cacheKey = `${keyBase}|${optionKey}`;
      const normalizedValues = optionValues.map((value) => this.normalizeLabel(value));
      const valueMetaMap = new Map();

      valueMetadata.forEach((meta) => {
        if (!meta) return;
        const candidates = [meta.value, meta.label];
        candidates.forEach((item) => {
          const normalized = this.normalizeLabel(item);
          if (normalized && !valueMetaMap.has(normalized)) {
            valueMetaMap.set(normalized, meta);
          }
        });
      });

      const cachedMap = this.colorImageCache.get(cacheKey);
      if (cachedMap) {
        const subset = {};
        let hasAll = true;
        normalizedValues.forEach((value) => {
          if (cachedMap[value]) {
            subset[value] = cachedMap[value];
          } else {
            hasAll = false;
          }
        });
        if (hasAll) {
          return subset;
        }
      }

      const product = await this.getProduct(productHandle, productData);
      if (!product) return cachedMap || null;

      const colorMap = cachedMap ? { ...cachedMap } : {};

      const optionIndex = this.findOptionIndex(product, optionName);
      const colorIndices = [];

      if (optionIndex !== -1) {
        colorIndices.push(optionIndex);
      } else {
        const colorIndex = this.findColorOptionIndex(product);
        if (colorIndex !== -1) colorIndices.push(colorIndex);
      }

      if (colorIndices.length && Array.isArray(product.variants)) {
        colorIndices.forEach((index) => {
          product.variants.forEach((variant) => {
            const value = variant[`option${index + 1}`];
            const normalizedValue = this.normalizeLabel(value);
            if (!normalizedValue) return;

            const variantImage = this.extractVariantImage(variant);
            if (variantImage) {
              colorMap[normalizedValue] = {
                image: variantImage,
                alt: variant?.featured_image?.alt || variant?.title || product.title,
                variantId: variant.id,
                source: 'product'
              };
            }
          });
        });
      }

      const unresolved = normalizedValues.filter((value) => !colorMap[value]);

      if (unresolved.length) {
        const components = await this.prepareBundleComponents(product);
        if (components.length) {
          await Promise.all(components.map((component) => this.ensureComponentData(component)));

          const preferredHandles = [];
          if (bundleComponent) {
            preferredHandles.push(bundleComponent);
          }

          const preferredTitles = [];
          if (bundleComponentTitle) {
            preferredTitles.push(bundleComponentTitle);
          }

          for (const unresolvedValue of unresolved) {
            const normalizedIndex = normalizedValues.indexOf(unresolvedValue);
            const originalValue = optionValues[normalizedIndex];
            const meta = valueMetaMap.get(unresolvedValue) || null;
            const handles = preferredHandles.slice();
            const titles = preferredTitles.slice();

            if (meta?.component) {
              handles.unshift(meta.component);
            }

            if (meta?.label) {
              titles.unshift(meta.label);
            }

            const media = await this.findComponentMedia(components, originalValue, optionName, {
              preferredHandles: handles,
              preferredTitles: titles
            });
            if (media?.image) {
              colorMap[unresolvedValue] = Object.assign({ source: 'bundle' }, media);
            }
          }
        }
      }

      this.colorImageCache.set(cacheKey, colorMap);

      const result = {};
      normalizedValues.forEach((value) => {
        if (colorMap[value]) {
          result[value] = colorMap[value];
        }
      });

      return result;
    }

    extractVariantImage(variant) {
      if (!variant) return null;
      if (variant.featured_image?.src) return variant.featured_image.src;
      if (typeof variant.featured_image === 'string') return variant.featured_image;
      if (variant.image) return variant.image;
      return null;
    }

    async resolveVariantMedia({ productHandle, productId, variantId, optionName, optionValue, productData }) {
      const cacheKey = `${productHandle || productId}|${variantId || optionName}|${optionValue || ''}`;
      if (this.variantMediaCache.has(cacheKey)) {
        return this.variantMediaCache.get(cacheKey);
      }

      const product = await this.getProduct(productHandle || productId, productData || this.getContextProductData(productHandle, productId));
      if (!product) {
        this.variantMediaCache.set(cacheKey, null);
        return null;
      }

      let variant = null;
      if (variantId && Array.isArray(product.variants)) {
        variant = product.variants.find((item) => String(item.id) === String(variantId));
      }

      if (variant) {
        const variantImage = this.extractVariantImage(variant);
        if (variantImage) {
          const media = {
            image: variantImage,
            alt: variant?.featured_image?.alt || variant?.title || product.title,
            variantId: variant.id,
            source: 'product'
          };
          this.variantMediaCache.set(cacheKey, media);
          return media;
        }
      }

      const candidateValues = [];
      if (variant) {
        const optionIndex = optionName ? this.findOptionIndex(product, optionName) : -1;
        if (optionIndex !== -1) {
          candidateValues.push(variant[`option${optionIndex + 1}`]);
        } else {
          const colorIndex = this.findColorOptionIndex(product);
          if (colorIndex !== -1) {
            candidateValues.push(variant[`option${colorIndex + 1}`]);
          }
        }
      }

      if (optionValue) {
        candidateValues.push(optionValue);
      }

      const colorImages = await this.getColorImages({
        productHandle: product.handle || productHandle || productId,
        optionName,
        optionValues: candidateValues,
        productData: product
      });

      let media = null;
      if (colorImages) {
        for (const value of candidateValues) {
          const normalized = this.normalizeLabel(value);
          if (colorImages[normalized]) {
            media = Object.assign({}, colorImages[normalized]);
            break;
          }
        }
      }

      if (!media && variant) {
        const fallbackImage = this.extractVariantImage(variant);
        if (fallbackImage) {
          media = {
            image: fallbackImage,
            alt: variant?.title || product.title,
            variantId: variant.id,
            source: 'product'
          };
        }
      }

      this.variantMediaCache.set(cacheKey, media);
      return media;
    }

    async prepareBundleComponents(product) {
      if (!product) return [];

      const parentKey = product.handle || product.id || this.slugify(product.title);
      if (parentKey && this.bundleComponentsCache.has(parentKey)) {
        return this.bundleComponentsCache.get(parentKey);
      }

      const components = [];
      const seenHandles = new Set();

      const mergeOptionTokens = (component, tokens = []) => {
        if (!component) return;
        if (!Array.isArray(component.optionNames)) {
          component.optionNames = [];
        }
        tokens
          .map((token) => this.normalizeLabel(token))
          .filter(Boolean)
          .forEach((token) => {
            if (!component.optionNames.includes(token)) {
              component.optionNames.push(token);
            }
          });
      };

      const addComponent = (candidate, meta = {}) => {
        const raw = candidate || meta.raw || {};

        let handle = meta.handle
          || raw?.product?.handle
          || raw?.handle
          || raw?.product_handle
          || raw?.product?.product_handle
          || raw?.selected_product?.handle
          || null;

        let title = meta.title
          || raw?.product?.title
          || raw?.product_title
          || raw?.title
          || (typeof candidate === 'string' ? candidate : null);

        if (!handle && title) {
          handle = this.slugify(title);
        }

        if (!title && handle) {
          title = handle.replace(/-/g, ' ');
        }

        if (!handle) return;

        const normalizedHandle = handle.toString().toLowerCase();
        const normalizedParent = parentKey ? parentKey.toString().toLowerCase() : null;
        if (normalizedParent && normalizedParent === normalizedHandle) {
          return;
        }

        if (seenHandles.has(normalizedHandle)) {
          const existing = components.find((item) => item.handle.toLowerCase() === normalizedHandle);
          if (existing) {
            if (title && (!existing.title || existing.title === existing.handle)) {
              existing.title = title;
              existing.normalizedTitle = this.normalizeLabel(title) || existing.normalizedTitle;
            }
            if (!existing.productId) {
              existing.productId = meta.productId || raw?.product?.id || raw?.product_id || raw?.id || null;
            }
            mergeOptionTokens(existing, [meta.optionName, raw?.option_name, raw?.optionTitle, raw?.option_title]);
          }
          return;
        }

        const entry = {
          handle: handle.toString(),
          title: title || handle,
          normalizedTitle: this.normalizeLabel(title || handle),
          productId: meta.productId || raw?.product?.id || raw?.product_id || raw?.id || null,
          raw: candidate,
          optionNames: []
        };

        mergeOptionTokens(entry, [meta.optionName, raw?.option_name, raw?.optionTitle, raw?.option_title]);

        seenHandles.add(normalizedHandle);
        components.push(entry);
      };

      const arraysToCheck = [
        product.bundle_components,
        product.bundleComponents,
        product.bundle_components?.nodes,
        product.components,
        product.bundle?.components
      ];

      arraysToCheck.forEach((entry) => {
        if (Array.isArray(entry)) {
          entry.forEach((item) => addComponent(item));
        }
      });

      if (product.metafields) {
        Object.values(product.metafields).forEach((namespace) => {
          if (!namespace) return;
          if (Array.isArray(namespace)) {
            namespace.forEach((item) => addComponent(item));
            return;
          }
          Object.values(namespace).forEach((meta) => {
            const value = meta?.value || meta;
            if (Array.isArray(value)) {
              value.forEach((item) => addComponent(item));
            } else if (typeof value === 'string' && value.includes('{')) {
              try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) parsed.forEach((item) => addComponent(item));
                if (parsed?.components && Array.isArray(parsed.components)) parsed.components.forEach((item) => addComponent(item));
              } catch (error) {
                // Ignore invalid JSON payloads
              }
            }
          });
        });
      }

      const guessedComponents = this.guessBundleComponentsFromProduct(product);
      guessedComponents.forEach((guess) => {
        addComponent(null, {
          handle: guess.handle,
          title: guess.title,
          optionName: guess.optionName,
          raw: { source: 'guess' }
        });
      });

      this.bundleComponentsCache.set(parentKey, components);
      return components;
    }

    async ensureComponentData(component) {
      if (!component || component.productData) return component?.productData || null;

      const key = component.handle;
      if (!key) return null;

      if (!this.componentPromises.has(key)) {
        const promise = this.getProduct(key).then((product) => {
          component.productData = product;
          if (product) {
            component.colorOptionIndex = this.findColorOptionIndex(product);
          }
          return product;
        });
        this.componentPromises.set(key, promise);
      }

      return this.componentPromises.get(key);
    }

    matchComponentsByOptionName(components, optionName) {
      if (!optionName) return [];
      const normalized = this.normalizeLabel(optionName);
      if (!normalized) return [];

      return components.filter((component) => {
        if (!component) return false;
        if (Array.isArray(component.optionNames)) {
          const optionMatch = component.optionNames.some((token) => normalized.includes(token));
          if (optionMatch) return true;
        }
        if (component.normalizedTitle && normalized.includes(component.normalizedTitle)) return true;
        const raw = component.raw;
        const rawTitle = raw?.option_title || raw?.title;
        if (rawTitle) {
          const normalizedRaw = this.normalizeLabel(rawTitle);
          if (normalized.includes(normalizedRaw)) return true;
        }
        return false;
      });
    }

    async findComponentMedia(components, optionValue, optionName, options = {}) {
      if (!components.length) return null;

      const { preferredHandles = [], preferredTitles = [] } = options || {};

      const candidates = this.generateValueCandidates(optionValue);
      const prioritized = this.matchComponentsByOptionName(components, optionName);
      let orderedComponents = prioritized.length ? prioritized.slice() : components.slice();

      const normalizedHandlePriority = Array.from(new Set(preferredHandles.filter(Boolean).map((handle) => handle
        .toString()
        .trim()
        .toLowerCase())));

      if (normalizedHandlePriority.length) {
        const handleMatched = [];
        const remaining = [];
        orderedComponents.forEach((component) => {
          const handle = component?.handle?.toString().toLowerCase();
          if (handle && normalizedHandlePriority.includes(handle)) {
            handleMatched.push(component);
          } else {
            remaining.push(component);
          }
        });
        orderedComponents = [...handleMatched, ...remaining];
      }

      const normalizedTitlePriority = Array.from(new Set(preferredTitles
        .filter(Boolean)
        .map((title) => this.normalizeLabel(title))
        .filter(Boolean)));

      if (normalizedTitlePriority.length) {
        normalizedTitlePriority.forEach((title) => candidates.add(title));

        const titleMatched = [];
        const remaining = [];
        orderedComponents.forEach((component) => {
          const titleCandidates = [component?.normalizedTitle, component?.title, component?.raw?.title]
            .map((entry) => this.normalizeLabel(entry));
          const optionTokens = Array.isArray(component?.optionNames) ? component.optionNames : [];
          const hasMatch = titleCandidates.some((value) => value && normalizedTitlePriority.includes(value))
            || optionTokens.some((token) => normalizedTitlePriority.includes(token));
          if (hasMatch) {
            titleMatched.push(component);
          } else {
            remaining.push(component);
          }
        });
        orderedComponents = [...titleMatched, ...remaining];
      }

      for (const component of orderedComponents) {
        if (!component?.productData) continue;

        const product = component.productData;
        const colorIndex = typeof component.colorOptionIndex === 'number'
          ? component.colorOptionIndex
          : this.findColorOptionIndex(product);

        if (colorIndex === -1) continue;

        const variants = Array.isArray(product.variants) ? product.variants : [];
        for (const variant of variants) {
          const variantValue = variant[`option${colorIndex + 1}`];
          const normalizedVariant = this.normalizeLabel(variantValue);
          if (!normalizedVariant) continue;

          const matches = candidates.some((candidate) => normalizedVariant === candidate || normalizedVariant.includes(candidate));
          if (matches) {
            const image = this.extractVariantImage(variant);
            if (image) {
              return {
                image,
                alt: `${product.title || component.title} - ${variant.title || variantValue}`,
                variantId: variant.id,
                component: component.handle
              };
            }
          }
        }
      }

      return null;
    }

    generateValueCandidates(value) {
      const candidates = new Set();
      const push = (val) => {
        const normalized = this.normalizeLabel(val);
        if (normalized) candidates.add(normalized);
      };

      push(value);

      if (typeof value === 'string') {
        const slashParts = value.split('/');
        slashParts.forEach(push);

        const dashParts = value.split(/\s[-–—]\s/);
        if (dashParts.length > 1) {
          dashParts.forEach(push);
        }

        const parenthesis = value.match(/\(([^)]+)\)/);
        if (parenthesis && parenthesis[1]) {
          push(parenthesis[1]);
        }

        const colonParts = value.split(':');
        if (colonParts.length > 1) {
          push(colonParts[colonParts.length - 1]);
        }
      }

      return Array.from(candidates);
    }

    applyImageToSwatch(swatch, media) {
      if (!swatch) return;

      let image = swatch.querySelector('img');
      const size = this.getSwatchSize(swatch);
      const sizedUrl = this.buildSizedImageUrl(media.image, size);

      swatch.classList.remove('bz-swatch--bundle', 'beezfy-swatch--bundle', 'beezfy-swatch--textual');

      if (!image) {
        image = document.createElement('img');
        image.className = 'bz-swatch__image beezfy-swatch__image beezfy-swatch__image--dynamic';
        image.loading = 'lazy';
        swatch.insertBefore(image, swatch.firstChild);
      }

      image.src = sizedUrl;
      if (window.Shopify?.Image?.getSizedImageUrl) {
        image.srcset = `${Shopify.Image.getSizedImageUrl(media.image, `${size}x`)} 1x, ${Shopify.Image.getSizedImageUrl(media.image, `${size * 2}x`)} 2x`;
      }
      image.alt = media.alt || swatch.dataset.optionValue || '';

      swatch.dataset.swatchImage = media.image;
      swatch.dataset.swatchColor = '';
      if (media.variantId) swatch.dataset.variantId = media.variantId;
      if (media.component) swatch.dataset.bundleComponent = media.component;

      const colorFallback = swatch.querySelector('.beezfy-swatch__color, .bz-swatch__color');
      if (colorFallback) {
        colorFallback.remove();
      }

      const textFallback = swatch.querySelector('.beezfy-swatch__text');
      if (textFallback) {
        textFallback.setAttribute('aria-hidden', 'true');
        textFallback.style.display = 'none';
        textFallback.classList.remove('beezfy-swatch__text--bundle');
      }
    }

    applyTextFallback(swatch, value) {
      if (!swatch) return;
      let text = swatch.querySelector('.beezfy-swatch__text');
      if (!text) {
        text = document.createElement('span');
        text.className = 'beezfy-swatch__text';
        swatch.appendChild(text);
      }
      const container = swatch.closest('[data-swatches-container]');
      const isBundle = container?.dataset?.isBundle === 'true';
      const labelSource = swatch.dataset.swatchLabel || value;
      const normalizedLabel = (labelSource || '').toString().trim();
      const shortLabel = normalizedLabel.slice(0, 3).toUpperCase();

      if (isBundle) {
        swatch.classList.add('bz-swatch--bundle', 'beezfy-swatch--bundle', 'beezfy-swatch--textual');
        text.classList.add('beezfy-swatch__text--bundle');
        text.textContent = normalizedLabel;
      } else {
        swatch.classList.remove('bz-swatch--bundle', 'beezfy-swatch--bundle', 'beezfy-swatch--textual');
        text.classList.remove('beezfy-swatch__text--bundle');
        text.textContent = shortLabel || normalizedLabel;
      }

      text.removeAttribute('aria-hidden');
      text.style.display = '';

      const image = swatch.querySelector('img');
      if (image) {
        image.remove();
      }

      const colorFallback = swatch.querySelector('.beezfy-swatch__color, .bz-swatch__color');
      if (colorFallback) {
        colorFallback.remove();
      }

      swatch.dataset.swatchImage = '';
      swatch.dataset.swatchColor = '';
    }

    getSwatchSize(swatch) {
      const width = swatch.style?.width || swatch.getAttribute('style');
      if (width) {
        const match = width.toString().match(/(\d+)/);
        if (match) return parseInt(match[1], 10);
      }
      return 48;
    }

    buildSizedImageUrl(url, size) {
      if (!url) return url;
      if (window.Shopify?.Image?.getSizedImageUrl) {
        return Shopify.Image.getSizedImageUrl(url, `${size}x`);
      }
      return url;
    }

    findOptionIndex(product, optionName) {
      if (!product || !optionName) return -1;
      const normalized = this.normalizeLabel(optionName);
      const attributeToken = this.extractAttributeToken(optionName);
      const targetTokens = this.extractOptionTokens(optionName);
      const options = Array.isArray(product.options) ? product.options : [];

      for (let index = 0; index < options.length; index += 1) {
        const option = options[index];
        const optionLabel = typeof option === 'string' ? option : option?.name;
        const optionNormalized = this.normalizeLabel(optionLabel);
        if (!optionNormalized) continue;

        const optionTokens = this.extractOptionTokens(optionLabel);
        if (attributeToken && optionTokens.includes(attributeToken)) {
          return index;
        }

        if (targetTokens.length && optionTokens.some((token) => targetTokens.includes(token))) {
          return index;
        }

        if (optionNormalized && (optionNormalized === normalized || normalized.includes(optionNormalized) || optionNormalized.includes(normalized))) {
          return index;
        }
      }

      return -1;
    }

    findColorOptionIndex(product) {
      if (!product) return -1;
      const options = Array.isArray(product.options) ? product.options : [];
      for (let index = 0; index < options.length; index += 1) {
        const option = options[index];
        const optionLabel = typeof option === 'string' ? option : option?.name;
        if (this.isColorOptionName(optionLabel)) {
          return index;
        }
      }
      return -1;
    }

    isColorOptionName(optionName) {
      if (!optionName) return false;
      const tokens = this.extractOptionTokens(optionName);
      if (!tokens.length) return false;
      if (tokens.some((token) => this.colorAttributeNames.includes(token))) return true;
      return tokens.some((token) => this.colorOptionNames.includes(token));
    }
  }

  // Initialize the system
  if (!window.beezfySwatchSystem) {
    window.beezfySwatchSystem = new BeezfySwatchSystem();
  }

  if (!window.BeezfySwatchDataStore) {
    window.BeezfySwatchDataStore = new BeezfySwatchDataStore(window.beezfySwatchSystem);
  }

  // Export for use in other scripts
  window.BeezfySwatchSystem = BeezfySwatchSystem;
  window.BeezfySwatchDataStoreClass = BeezfySwatchDataStore;
}

// Beezfy Swatch System loaded
